import { useState } from "react";
import { toast } from "react-toastify";
import useTranslation from "../../i18n/useTranslation";
import {getLocalizedGraphQLError} from "../../util/graphqlError";
import DeletePlayerMutation from "./mutations/DeletePlayer";
import { confirmDelete } from "../common/confirmDelete";

function TeamCoaches({ coaches = [], colors, onAddCoach, onCoachDeleted }) {
    const { t } = useTranslation();
    const [deletePlayer] = DeletePlayerMutation();
    const [deletingCoachId, setDeletingCoachId] = useState(null);

    const handleDeleteCoach = (coach) => {
        confirmDelete({
            title: t("teamCoaches.deleteTitle"),
            message: t("teamCoaches.deleteMessage", { name: coach?.fullName || "" }),
            confirmLabel: t("teamCoaches.deleteConfirm"),
            cancelLabel: t("common.cancel"),
            danger: true,
            onConfirm: async () => {
                try {
                    setDeletingCoachId(coach.id);
                    const { data } = await deletePlayer({
                        variables: { rosterId: Number(coach.id) },
                    });

                    if (!data?.deletePlayer?.success) {
                        toast.error(t("teamCoaches.deleteError"), {
                            autoClose: 3000,
                            position: "top-center",
                            closeOnClick: true,
                        });
                        return;
                    }

                    if (onCoachDeleted) onCoachDeleted(coach.id);

                    toast.success(t("teamCoaches.deleteSuccess"), {
                        autoClose: 2000,
                        position: "top-center",
                        closeOnClick: true,
                    });
                } catch (err) {
                    toast.error(getLocalizedGraphQLError(err, t, "teamCoaches.deleteError"), {
                        autoClose: 3000,
                        position: "top-center",
                        closeOnClick: true,
                    });
                } finally {
                    setDeletingCoachId(null);
                }
            },
        });
    };


    return (
        <div
            className="card mb-4 border-0"
            style={{
                borderRadius: "18px",
                overflow: "hidden",
                backgroundColor: colors.white,
                border: `1px solid ${colors.borderColor}`,
            }}
        >
            {/* Header */}
            <div
                className="d-flex align-items-center justify-content-between"
                style={{
                    backgroundColor: colors.primary,
                    color: colors.textOnPrimary,
                    padding: "16px 20px",
                }}
            >
                <div>
                    <h5 className="mb-0 fw-bold">
                        {t("refereeList.roles.coaches")}
                    </h5>
                    <small style={{ opacity: 0.85 }}>
                        {coaches.length}{" "}
                        {coaches.length === 1
                            ? t("editGameModal.labels.coach")
                            : t("refereeList.roles.coaches")}
                    </small>
                </div>

                <button
                    type="button"
                    className="btn"
                    onClick={onAddCoach}
                    style={{color: colors.textOnSecondary, backgroundColor: colors.secondary}}
                >
                    <i className="fas fa-plus mr-1" />
                    {t("teamMemberPicker.addCoach")}
                </button>
            </div>

            {/* Body */}
            <div style={{ padding: "18px" }}>
                <div className="row g-3">
                    {coaches.map((coach) => (
                        <div key={coach.id} className="col-12 col-md-6 col-xl-4">
                            <div
                                className="d-flex align-items-center"
                                style={{
                                    borderRadius: "14px",
                                    margin: "5px",
                                    padding: "14px 16px",
                                    // backgroundColor: colors.primary,
                                    border: `1px solid ${colors.secondary}`,
                                    gap: "12px",
                                    transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor =
                                        colors.accentColor;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor =
                                        colors.borderColor;
                                }}
                            >
                                {/* Avatar */}
                                <div
                                    className="d-flex align-items-center justify-content-center fw-bold"
                                    style={{
                                        width: "46px",
                                        height: "46px",
                                        borderRadius: "50%",
                                        backgroundColor: colors.secondary,
                                        color: colors.woodsmoke,
                                        fontSize: "16px",
                                        flexShrink: 0,
                                    }}
                                >
                                    {coach.fullName
                                        ?.split(" ")
                                        ?.map((n) => n[0])
                                        ?.slice(0, 2)
                                        ?.join("")
                                        ?.toUpperCase() || "C"}
                                </div>

                                {/* Info */}
                                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                    <div
                                        className="fw-semibold text-truncate"
                                        style={{
                                            color: colors.woodsmoke,
                                            fontSize: "15px",
                                        }}
                                    >
                                        {coach.fullName}
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => handleDeleteCoach(coach)}
                                    disabled={deletingCoachId !== null || String(coach.id).startsWith("tmp-")}
                                    title={t("teamCoaches.deleteConfirm")}
                                    aria-label={t("teamCoaches.deleteConfirm")}
                                >
                                    {deletingCoachId === coach.id ? (
                                        <i className="fas fa-spinner fa-spin" />
                                    ) : (
                                        <i className="fas fa-trash" />
                                    )}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default TeamCoaches;
